import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { LoadingOverlay } from '@mantine/core';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import ComingSoon from '@/pages/ComingSoon';
import AuthGuard from '@/components/AuthGuard';
import PermissionGuard from '@/components/PermissionGuard';

// ---------------------------------------------------------------------------
// Retry wrapper for lazy imports — handles stale chunks after deploys.
// On dynamic import failure, reloads the page once to fetch new asset manifest.
// Uses sessionStorage to prevent infinite reload loops.
// ---------------------------------------------------------------------------
function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err) => {
      const key = 'chunk-reload';
      const hasReloaded = sessionStorage.getItem(key);
      if (!hasReloaded) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        // Return a never-resolving promise so React doesn't render the error
        return new Promise(() => {});
      }
      sessionStorage.removeItem(key);
      throw err;
    }),
  );
}

// Lazy-loaded page components for code splitting
const Products = lazyRetry(() => import('@/pages/inventory/Products'));
const StockLevels = lazyRetry(() => import('@/pages/inventory/StockLevels'));
const Receiving = lazyRetry(() => import('@/pages/inventory/Receiving'));
const Pallets = lazyRetry(() => import('@/pages/inventory/Pallets'));
const Expiration = lazyRetry(() => import('@/pages/inventory/Expiration'));
const Orders = lazyRetry(() => import('@/pages/sales/Orders'));
const NewOrder = lazyRetry(() => import('@/pages/sales/NewOrder'));
const Customers = lazyRetry(() => import('@/pages/sales/Customers'));
const CreditMemos = lazyRetry(() => import('@/pages/sales/CreditMemos'));
const Payments = lazyRetry(() => import('@/pages/sales/Payments'));
const OrderIntakeQueue = lazyRetry(() => import('@/pages/sales/OrderIntakeQueue'));
const AICustomerDashboard = lazyRetry(() => import('@/pages/sales/AICustomerDashboard'));
const PurchaseOrders = lazyRetry(() => import('@/pages/purchasing/PurchaseOrders'));
const Vendors = lazyRetry(() => import('@/pages/purchasing/Vendors'));
const LandedCosts = lazyRetry(() => import('@/pages/purchasing/LandedCosts'));
const VendorCatalog = lazyRetry(() => import('@/pages/purchasing/VendorCatalog'));
const GLEntries = lazyRetry(() => import('@/pages/accounting/GLEntries'));
const AccountingReports = lazyRetry(() => import('@/pages/accounting/Reports'));
const JournalEntries = lazyRetry(() => import('@/pages/accounting/JournalEntries'));
const NewJournalEntry = lazyRetry(() => import('@/pages/accounting/NewJournalEntry'));
const TrialBalance = lazyRetry(() => import('@/pages/accounting/TrialBalance'));
const BankReconciliation = lazyRetry(() => import('@/pages/accounting/BankReconciliation'));
const FixedAssets = lazyRetry(() => import('@/pages/accounting/FixedAssets'));
const Budgets = lazyRetry(() => import('@/pages/accounting/Budgets'));
const CheckSetup = lazyRetry(() => import('@/pages/accounting/CheckSetup'));
const CPAAuditPackage = lazyRetry(() => import('@/pages/accounting/CPAAuditPackage'));
const AuditReadiness = lazyRetry(() => import('@/pages/accounting/AuditReadiness'));
const BankStatementImport = lazyRetry(() => import('@/pages/accounting/BankStatementImport'));
const InvestmentPortfolio = lazyRetry(() => import('@/pages/accounting/InvestmentPortfolio'));
const Documents = lazyRetry(() => import('@/pages/admin/Documents'));
const PhysicalCount = lazyRetry(() => import('@/pages/warehouse/PhysicalCount'));
const CreditManagement = lazyRetry(() => import('@/pages/accounting/CreditManagement'));
const PayStubs = lazyRetry(() => import('@/pages/payroll/PayStubs'));
const CustomerPortal = lazyRetry(() => import('@/pages/sales/CustomerPortal'));
const VendorPortal = lazyRetry(() => import('@/pages/purchasing/VendorPortal'));
const FinancialDashboard = lazyRetry(() => import('@/pages/reports/FinancialDashboard'));
const DailySummary = lazyRetry(() => import('@/pages/operations/DailySummary'));
const SystemHealth = lazyRetry(() => import('@/pages/admin/SystemHealth'));
const RecurringEntries = lazyRetry(() => import('@/pages/accounting/RecurringEntries'));
const Banking = lazyRetry(() => import('@/pages/accounting/Banking'));
const PayrollSetup = lazyRetry(() => import('@/pages/payroll/PayrollSetup'));
const CurrencyRates = lazyRetry(() => import('@/pages/accounting/CurrencyRates'));
const FiscalPeriods = lazyRetry(() => import('@/pages/accounting/FiscalPeriods'));
const TaxCompliance = lazyRetry(() => import('@/pages/accounting/TaxCompliance'));
const Employees = lazyRetry(() => import('@/pages/operations/Employees'));
const PickLists = lazyRetry(() => import('@/pages/operations/PickLists'));
const CycleCounts = lazyRetry(() => import('@/pages/operations/CycleCounts'));
const Shipping = lazyRetry(() => import('@/pages/operations/Shipping'));
const ReceivingDock = lazyRetry(() => import('@/pages/operations/Receiving'));
const ColdChainMonitor = lazyRetry(() => import('@/pages/operations/ColdChainMonitor'));
const RouteOptimization = lazyRetry(() => import('@/pages/operations/RouteOptimization'));
const Scheduling = lazyRetry(() => import('@/pages/operations/Scheduling'));
const OperationalAlerts = lazyRetry(() => import('@/pages/operations/OperationalAlerts'));
const FoodSafety = lazyRetry(() => import('@/pages/compliance/FoodSafety'));
const PACA = lazyRetry(() => import('@/pages/compliance/PACA'));
const ColdChain = lazyRetry(() => import('@/pages/compliance/ColdChain'));
const HACCPPlans = lazyRetry(() => import('@/pages/compliance/HACCPPlans'));
const GAPInspections = lazyRetry(() => import('@/pages/compliance/GAPInspections'));
const GFSICertification = lazyRetry(() => import('@/pages/compliance/GFSICertification'));
const AllergenControl = lazyRetry(() => import('@/pages/compliance/AllergenControl'));
const EnvironmentalMonitoring = lazyRetry(() => import('@/pages/compliance/EnvironmentalMonitoring'));
const FoodDefense = lazyRetry(() => import('@/pages/compliance/FoodDefense'));
const FSMAControls = lazyRetry(() => import('@/pages/compliance/FSMAControls'));
const OrganicCOC = lazyRetry(() => import('@/pages/compliance/OrganicCOC'));
const PesticideMRL = lazyRetry(() => import('@/pages/compliance/PesticideMRL'));
const LGMACompliance = lazyRetry(() => import('@/pages/compliance/LGMACompliance'));
const AuditConnectors = lazyRetry(() => import('@/pages/compliance/AuditConnectors'));
const InternalAudit = lazyRetry(() => import('@/pages/compliance/InternalAudit'));
const Calibration = lazyRetry(() => import('@/pages/compliance/Calibration'));
const ChangeControl = lazyRetry(() => import('@/pages/compliance/ChangeControl'));
const CustomerCompliance = lazyRetry(() => import('@/pages/compliance/CustomerCompliance'));
const Contacts = lazyRetry(() => import('@/pages/crm/Contacts'));
const Activities = lazyRetry(() => import('@/pages/crm/Activities'));
const PriceManagement = lazyRetry(() => import('@/pages/pricing/PriceManagement'));
const Forecasting = lazyRetry(() => import('@/pages/forecasting/Forecasting'));
const Dashboards = lazyRetry(() => import('@/pages/reports/Dashboards'));
const AdminHub = lazyRetry(() => import('@/pages/admin/AdminHub'));
const Users = lazyRetry(() => import('@/pages/admin/Users'));
const Roles = lazyRetry(() => import('@/pages/admin/Roles'));
const AuditLog = lazyRetry(() => import('@/pages/admin/AuditLog'));
const AdminSettings = lazyRetry(() => import('@/pages/admin/Settings'));
const ApiTokens = lazyRetry(() => import('@/pages/admin/ApiTokens'));
const AdminWebhooks = lazyRetry(() => import('@/pages/admin/Webhooks'));
const Tenants = lazyRetry(() => import('@/pages/admin/Tenants'));
const NotFound = lazyRetry(() => import('@/pages/NotFound'));
const ForgotPassword = lazyRetry(() => import('@/pages/ForgotPassword'));
const PayrollDashboard = lazyRetry(() => import('@/pages/payroll/PayrollDashboard'));
const PayrollRuns = lazyRetry(() => import('@/pages/payroll/PayrollRuns'));
const TimeAttendance = lazyRetry(() => import('@/pages/payroll/TimeAttendance'));
const AdvancedPayroll = lazyRetry(() => import('@/pages/payroll/AdvancedPayroll'));
const APDashboard = lazyRetry(() => import('@/pages/ap/APDashboard'));
const InvoiceCapture = lazyRetry(() => import('@/pages/ap/InvoiceCapture'));
const CorporateCards = lazyRetry(() => import('@/pages/ap/CorporateCards'));
const CashForecast = lazyRetry(() => import('@/pages/ap/CashForecast'));
const ExceptionQueue = lazyRetry(() => import('@/pages/ap/ExceptionQueue'));
const FleetDashboard = lazyRetry(() => import('@/pages/fleet/FleetDashboard'));
const FleetMaintenance = lazyRetry(() => import('@/pages/fleet/FleetMaintenance'));
const DOTCompliance = lazyRetry(() => import('@/pages/fleet/DOTCompliance'));
const AssetTracking = lazyRetry(() => import('@/pages/fleet/AssetTracking'));
const DriverSafety = lazyRetry(() => import('@/pages/fleet/DriverSafety'));
const WorkflowTemplates = lazyRetry(() => import('@/pages/workflow/WorkflowTemplates'));
const WorkflowInstances = lazyRetry(() => import('@/pages/workflow/WorkflowInstances'));
const EDIManagement = lazyRetry(() => import('@/pages/integrations/EDIManagement'));
const DocumentIntelligence = lazyRetry(() => import('@/pages/ai/DocumentIntelligence'));
const PriceSheets = lazyRetry(() => import('@/pages/pricing/PriceSheets'));
const DynamicDiscounts = lazyRetry(() => import('@/pages/pricing/DynamicDiscounts'));
const Rebates = lazyRetry(() => import('@/pages/pricing/Rebates'));
const Recruiting = lazyRetry(() => import('@/pages/hr/Recruiting'));
const IoTSensors = lazyRetry(() => import('@/pages/iot/IoTSensors'));
const SpoilageRisk = lazyRetry(() => import('@/pages/inventory/SpoilageRisk'));
const ShrinkAnalytics = lazyRetry(() => import('@/pages/inventory/ShrinkAnalytics'));
const RecallManagement = lazyRetry(() => import('@/pages/compliance/RecallManagement'));
const ThreePLManagement = lazyRetry(() => import('@/pages/logistics/ThreePLManagement'));
const LogisticsML = lazyRetry(() => import('@/pages/logistics/LogisticsML'));
const AuditorPortal = lazyRetry(() => import('@/pages/audit/AuditorPortal'));
const AuditIntegrityDashboard = lazyRetry(() => import('@/pages/admin/AuditIntegrityDashboard'));
const SODAdministration = lazyRetry(() => import('@/pages/admin/SODAdministration'));
const AccessReview = lazyRetry(() => import('@/pages/admin/AccessReview'));
const EncryptionKeyManagement = lazyRetry(() => import('@/pages/admin/EncryptionKeyManagement'));
const DataRetention = lazyRetry(() => import('@/pages/admin/DataRetention'));
const PayrollTaxReconciliation = lazyRetry(() => import('@/pages/payroll/PayrollTaxReconciliation'));
const BenefitsReconciliation = lazyRetry(() => import('@/pages/payroll/BenefitsReconciliation'));
const SessionSecurity = lazyRetry(() => import('@/pages/admin/SessionSecurity'));
const BreakGlassAccess = lazyRetry(() => import('@/pages/admin/BreakGlassAccess'));
const DualApproval = lazyRetry(() => import('@/pages/admin/DualApproval'));
const ComplianceEvidence = lazyRetry(() => import('@/pages/audit/ComplianceEvidence'));
const PentestEvidence = lazyRetry(() => import('@/pages/admin/PentestEvidence'));
const SIEMIntegration = lazyRetry(() => import('@/pages/admin/SIEMIntegration'));
const DataClassification = lazyRetry(() => import('@/pages/admin/DataClassification'));
const ControlTesting = lazyRetry(() => import('@/pages/admin/ControlTesting'));
const PeriodCloseHub = lazyRetry(() => import('@/pages/admin/PeriodCloseHub'));

// Finance (new)
const PettyCash = lazyRetry(() => import('@/pages/finance/PettyCash'));
const Intercompany = lazyRetry(() => import('@/pages/finance/Intercompany'));
const FinancialClose = lazyRetry(() => import('@/pages/finance/FinancialClose'));
const LeaseAccounting = lazyRetry(() => import('@/pages/finance/LeaseAccounting'));
const CashApplication = lazyRetry(() => import('@/pages/finance/CashApplication'));
const ConsolidatedTax = lazyRetry(() => import('@/pages/finance/ConsolidatedTax'));
const DeferredTax = lazyRetry(() => import('@/pages/finance/DeferredTax'));
const CostCenters = lazyRetry(() => import('@/pages/finance/CostCenters'));
const RevenueRecognition = lazyRetry(() => import('@/pages/finance/RevenueRecognition'));
const BadDebt = lazyRetry(() => import('@/pages/finance/BadDebt'));
const APAging = lazyRetry(() => import('@/pages/finance/APAging'));
const APDuplicates = lazyRetry(() => import('@/pages/finance/APDuplicates'));
const BankImport = lazyRetry(() => import('@/pages/finance/BankImport'));
const PlaidFeeds = lazyRetry(() => import('@/pages/finance/PlaidFeeds'));
const CheckWire = lazyRetry(() => import('@/pages/finance/CheckWire'));
const PaymentLinksPage = lazyRetry(() => import('@/pages/finance/PaymentLinks'));
const PaymentOrchestrator = lazyRetry(() => import('@/pages/finance/PaymentOrchestrator'));
const PaymentTerms = lazyRetry(() => import('@/pages/finance/PaymentTerms'));
const ChargebacksPage = lazyRetry(() => import('@/pages/finance/Chargebacks'));
const CreditScoring = lazyRetry(() => import('@/pages/finance/CreditScoring'));
const ExpenseReimbursement = lazyRetry(() => import('@/pages/finance/ExpenseReimbursement'));
const Form8300 = lazyRetry(() => import('@/pages/finance/Form8300'));
const Form4562 = lazyRetry(() => import('@/pages/finance/Form4562'));
const ScheduleM1 = lazyRetry(() => import('@/pages/finance/ScheduleM1'));

// Reconciliation modules
const CreditCardReconciliation = lazyRetry(() => import('@/pages/finance/CreditCardReconciliation'));
const SalesTaxReconciliation = lazyRetry(() => import('@/pages/finance/SalesTaxReconciliation'));
const VendorStatementReconciliation = lazyRetry(() => import('@/pages/finance/VendorStatementReconciliation'));
const SubLedgerDashboard = lazyRetry(() => import('@/pages/accounting/SubLedgerDashboard'));
const FixedAssetReconciliation = lazyRetry(() => import('@/pages/accounting/FixedAssetReconciliation'));

// ERP expansion modules
const ProjectAccounting = lazyRetry(() => import('@/pages/finance/ProjectAccounting'));
const EmployeeSelfService = lazyRetry(() => import('@/pages/hr/EmployeeSelfService'));
const ESSAdmin = lazyRetry(() => import('@/pages/hr/ESSAdmin'));
const VendorPortalManagement = lazyRetry(() => import('@/pages/purchasing/VendorPortalManagement'));
const PurchaseRequisitions = lazyRetry(() => import('@/pages/purchasing/PurchaseRequisitions'));
const ReturnsManagement = lazyRetry(() => import('@/pages/sales/ReturnsManagement'));
const WarrantyTracking = lazyRetry(() => import('@/pages/sales/WarrantyTracking'));
const SalesDashboard = lazyRetry(() => import('@/pages/sales/SalesDashboard'));
const CRMPipeline = lazyRetry(() => import('@/pages/crm/Pipeline'));
const MultiBookAccounting = lazyRetry(() => import('@/pages/accounting/MultiBookAccounting'));
const SLATracking = lazyRetry(() => import('@/pages/operations/SLATracking'));
const ChangeManagement = lazyRetry(() => import('@/pages/operations/ChangeManagement'));

// Payroll (new)
const PayrollACH = lazyRetry(() => import('@/pages/payroll/PayrollACH'));
const PayrollRegister = lazyRetry(() => import('@/pages/payroll/PayrollRegister'));
const Garnishments = lazyRetry(() => import('@/pages/payroll/Garnishments'));
const RetroPay = lazyRetry(() => import('@/pages/payroll/RetroPay'));
const TipManagement = lazyRetry(() => import('@/pages/payroll/TipManagement'));
const InternationalPayroll = lazyRetry(() => import('@/pages/payroll/InternationalPayroll'));
const StateWages = lazyRetry(() => import('@/pages/payroll/StateWages'));
const SUTAFiling = lazyRetry(() => import('@/pages/payroll/SUTAFiling'));
const StateReciprocity = lazyRetry(() => import('@/pages/payroll/StateReciprocity'));
const CertifiedPayroll = lazyRetry(() => import('@/pages/payroll/CertifiedPayroll'));
const CSERemittance = lazyRetry(() => import('@/pages/payroll/CSERemittance'));
const NewHireReporting = lazyRetry(() => import('@/pages/payroll/NewHireReporting'));
const EarnedWageAccess = lazyRetry(() => import('@/pages/payroll/EarnedWageAccess'));
const Form940 = lazyRetry(() => import('@/pages/payroll/Form940'));
const Form941 = lazyRetry(() => import('@/pages/payroll/Form941'));
const Form943 = lazyRetry(() => import('@/pages/payroll/Form943'));
const W2Management = lazyRetry(() => import('@/pages/payroll/W2Management'));
const W4Tracking = lazyRetry(() => import('@/pages/payroll/W4Tracking'));
const Form1099 = lazyRetry(() => import('@/pages/payroll/Form1099'));
const PayrollImport = lazyRetry(() => import('@/pages/payroll/PayrollImport'));
const PayrollRunWizard = lazyRetry(() => import('@/pages/payroll/PayrollRunWizard'));
const NewPayrollRun = lazyRetry(() => import('@/pages/payroll/NewPayrollRun'));
const PayrollRunEmployees = lazyRetry(() => import('@/pages/payroll/PayrollRunEmployees'));
const PayrollReports = lazyRetry(() => import('@/pages/payroll/PayrollReports'));
const DirectDeposit = lazyRetry(() => import('@/pages/payroll/DirectDeposit'));
const PayrollAuditLog = lazyRetry(() => import('@/pages/payroll/PayrollAuditLog'));
const PayrollAlerts = lazyRetry(() => import('@/pages/payroll/PayrollAlerts'));
const PayrollAIAssistant = lazyRetry(() => import('@/pages/payroll/PayrollAIAssistant'));
const PayrollAnomalyDetection = lazyRetry(() => import('@/pages/payroll/PayrollAnomalyDetection'));
const PayrollBatchCorrections = lazyRetry(() => import('@/pages/payroll/PayrollBatchCorrections'));
const PayrollAutomation = lazyRetry(() => import('@/pages/payroll/PayrollAutomation'));
const PayrollAIApproval = lazyRetry(() => import('@/pages/payroll/PayrollAIApproval'));
const PayrollGLReconciliation = lazyRetry(() => import('@/pages/payroll/PayrollGLReconciliation'));
const BenefitsVendorRecon = lazyRetry(() => import('@/pages/payroll/BenefitsVendorRecon'));
const PayrollBankRecon = lazyRetry(() => import('@/pages/payroll/PayrollBankRecon'));

// HR (new)
const ACACompliance = lazyRetry(() => import('@/pages/hr/ACACompliance'));
const Retirement401k = lazyRetry(() => import('@/pages/hr/Retirement401k'));
const ERISACompliance = lazyRetry(() => import('@/pages/hr/ERISACompliance'));
const LeaveBenefits = lazyRetry(() => import('@/pages/hr/LeaveBenefits'));
const Onboarding = lazyRetry(() => import('@/pages/hr/Onboarding'));
const LMS = lazyRetry(() => import('@/pages/hr/LMS'));

// TMS & Fleet (new)
const TMSAdvanced = lazyRetry(() => import('@/pages/tms/TMSAdvanced'));
const TMSIntelligence = lazyRetry(() => import('@/pages/tms/TMSIntelligence'));
const FleetTMS = lazyRetry(() => import('@/pages/fleet/FleetTMS'));
const DriverPerformance = lazyRetry(() => import('@/pages/fleet/DriverPerformance'));
const RateShopping = lazyRetry(() => import('@/pages/tms/RateShopping'));
const FreightAudit = lazyRetry(() => import('@/pages/tms/FreightAudit'));
const LoadPlanning = lazyRetry(() => import('@/pages/tms/LoadPlanning'));
const RouteAccounting = lazyRetry(() => import('@/pages/tms/RouteAccounting'));
const DrugAlcoholTesting = lazyRetry(() => import('@/pages/fleet/DrugAlcoholTesting'));
const DashcamIntegration = lazyRetry(() => import('@/pages/fleet/DashcamIntegration'));

// Warehouse & Supply Chain (new)
const WMS = lazyRetry(() => import('@/pages/warehouse/WMS'));
const WarehouseAdvanced = lazyRetry(() => import('@/pages/warehouse/WarehouseAdvanced'));
const DockScheduling = lazyRetry(() => import('@/pages/warehouse/DockScheduling'));
const YardManagement = lazyRetry(() => import('@/pages/warehouse/YardManagement'));
const Slotting = lazyRetry(() => import('@/pages/warehouse/Slotting'));
const Allocation = lazyRetry(() => import('@/pages/warehouse/Allocation'));
const MRP = lazyRetry(() => import('@/pages/warehouse/MRP'));
const PackHouse = lazyRetry(() => import('@/pages/warehouse/PackHouse'));
const PalletBuild = lazyRetry(() => import('@/pages/warehouse/PalletBuild'));
const ShelfLife = lazyRetry(() => import('@/pages/warehouse/ShelfLife'));
const Robotics = lazyRetry(() => import('@/pages/warehouse/Robotics'));
const Grading = lazyRetry(() => import('@/pages/supply-chain/Grading'));
const CoolCompliance = lazyRetry(() => import('@/pages/supply-chain/CoolCompliance'));
const EthyleneCA = lazyRetry(() => import('@/pages/supply-chain/EthyleneCA'));
const Freshness = lazyRetry(() => import('@/pages/supply-chain/Freshness'));
const Trace = lazyRetry(() => import('@/pages/supply-chain/Trace'));
const MockRecall = lazyRetry(() => import('@/pages/supply-chain/MockRecall'));

// Commerce (new)
const ECommerce = lazyRetry(() => import('@/pages/commerce/ECommerce'));
const BrokerCommission = lazyRetry(() => import('@/pages/commerce/BrokerCommission'));
const LoyaltyRebates = lazyRetry(() => import('@/pages/commerce/LoyaltyRebates'));
const RFQ = lazyRetry(() => import('@/pages/commerce/RFQ'));
const RecurringOrders = lazyRetry(() => import('@/pages/commerce/RecurringOrders'));
const POS = lazyRetry(() => import('@/pages/commerce/POS'));
const Substitutions = lazyRetry(() => import('@/pages/commerce/Substitutions'));
const PriceLocks = lazyRetry(() => import('@/pages/commerce/PriceLocks'));
const PredictivePricing = lazyRetry(() => import('@/pages/commerce/PredictivePricing'));
const MenuPlanning = lazyRetry(() => import('@/pages/commerce/MenuPlanning'));
const CustomerPreferences = lazyRetry(() => import('@/pages/commerce/CustomerPreferences'));

// Compliance (new)
const Permits = lazyRetry(() => import('@/pages/compliance/Permits'));
const PACATrust = lazyRetry(() => import('@/pages/compliance/PACATrust'));
const Bioterrorism = lazyRetry(() => import('@/pages/compliance/Bioterrorism'));
const BCP = lazyRetry(() => import('@/pages/compliance/BCP'));
const CFATS = lazyRetry(() => import('@/pages/compliance/CFATS'));
const CarbonESG = lazyRetry(() => import('@/pages/compliance/CarbonESG'));
const CommodityAssessments = lazyRetry(() => import('@/pages/compliance/CommodityAssessments'));
const DataBreach = lazyRetry(() => import('@/pages/compliance/DataBreach'));
const FCRA = lazyRetry(() => import('@/pages/compliance/FCRA'));
const FDAFurls = lazyRetry(() => import('@/pages/compliance/FDAFurls'));
const FDARegistration = lazyRetry(() => import('@/pages/compliance/FDARegistration'));
const FSMATransport = lazyRetry(() => import('@/pages/compliance/FSMATransport'));
const ImportDuty = lazyRetry(() => import('@/pages/compliance/ImportDuty'));
const MarketingCompliance = lazyRetry(() => import('@/pages/compliance/MarketingCompliance'));
const OFACSanctions = lazyRetry(() => import('@/pages/compliance/OFACSanctions'));

// Regulatory (new)
const OrganicEnforcement = lazyRetry(() => import('@/pages/regulatory/OrganicEnforcement'));
const PCICompliance = lazyRetry(() => import('@/pages/regulatory/PCICompliance'));
const Phytosanitary = lazyRetry(() => import('@/pages/regulatory/Phytosanitary'));
const Privacy = lazyRetry(() => import('@/pages/regulatory/Privacy'));
const StateOrganic = lazyRetry(() => import('@/pages/regulatory/StateOrganic'));
const USERRA = lazyRetry(() => import('@/pages/regulatory/USERRA'));
const WARNAct = lazyRetry(() => import('@/pages/regulatory/WARNAct'));
const WeightsMeasures = lazyRetry(() => import('@/pages/regulatory/WeightsMeasures'));

// VA Tax (new)
const VirginiaCompliance = lazyRetry(() => import('@/pages/va-tax/VirginiaCompliance'));
const VAST1 = lazyRetry(() => import('@/pages/va-tax/VAST1'));
const VA4Certificate = lazyRetry(() => import('@/pages/va-tax/VA4Certificate'));
const VA5Filing = lazyRetry(() => import('@/pages/va-tax/VA5Filing'));
const VA15Deposit = lazyRetry(() => import('@/pages/va-tax/VA15Deposit'));
const VA16Reconciliation = lazyRetry(() => import('@/pages/va-tax/VA16Reconciliation'));
const VAWithholding = lazyRetry(() => import('@/pages/va-tax/VAWithholding'));
const VACorporateTax = lazyRetry(() => import('@/pages/va-tax/VACorporateTax'));
const VAIFTA = lazyRetry(() => import('@/pages/va-tax/VAIFTA'));
const VAFilingFrequency = lazyRetry(() => import('@/pages/va-tax/VAFilingFrequency'));
const VAPenaltyInterest = lazyRetry(() => import('@/pages/va-tax/VAPenaltyInterest'));
const VATangibleProperty = lazyRetry(() => import('@/pages/va-tax/VATangibleProperty'));
const VATaxIntelligence = lazyRetry(() => import('@/pages/va-tax/VATaxIntelligence'));
const VDACS = lazyRetry(() => import('@/pages/va-tax/VDACS'));
const VDHHealth = lazyRetry(() => import('@/pages/va-tax/VDHHealth'));

// AI (new)
const AICopilot = lazyRetry(() => import('@/pages/ai/AICopilot'));
const AIEmailParsing = lazyRetry(() => import('@/pages/ai/AIEmailParsing'));
const AIQuality = lazyRetry(() => import('@/pages/ai/AIQuality'));
const AIOrderChat = lazyRetry(() => import('@/pages/ai/AIOrderChat'));
const MLForecasting = lazyRetry(() => import('@/pages/ai/MLForecasting'));
const DemandIntelligence = lazyRetry(() => import('@/pages/ai/DemandIntelligence'));
const MarketIntelligence = lazyRetry(() => import('@/pages/ai/MarketIntelligence'));
const VoiceAgent = lazyRetry(() => import('@/pages/ai/VoiceAgent'));
const VoiceAgentLive = lazyRetry(() => import('@/pages/ai/VoiceAgentLive'));
const VoiceMetrics = lazyRetry(() => import('@/pages/ai/VoiceMetrics'));
const VoicePromptEditor = lazyRetry(() => import('@/pages/ai/VoicePromptEditor'));
const FinancialIntelligence = lazyRetry(() => import('@/pages/ai/FinancialIntelligence'));
const VendorCostIntelligence = lazyRetry(() => import('@/pages/ai/VendorCostIntelligence'));
const ProfitOptimization = lazyRetry(() => import('@/pages/ai/ProfitOptimization'));
const AITalentMatching = lazyRetry(() => import('@/pages/ai/AITalentMatching'));
const AIRetentionRisk = lazyRetry(() => import('@/pages/ai/AIRetentionRisk'));
const AIWorkforcePlanning = lazyRetry(() => import('@/pages/ai/AIWorkforcePlanning'));
const CustomerTimeline = lazyRetry(() => import('@/pages/ai/CustomerTimeline'));
const EmailMonitorV2 = lazyRetry(() => import('@/pages/ai/EmailMonitorV2'));
const EmailMonitorMetrics = lazyRetry(() => import('@/pages/ai/EmailMonitorMetrics'));
const AILeadScoring = lazyRetry(() => import('@/pages/ai/AILeadScoring'));
const AIChurnPrevention = lazyRetry(() => import('@/pages/ai/AIChurnPrevention'));
const AISalesForecasting = lazyRetry(() => import('@/pages/ai/AISalesForecasting'));
const AIPickOptimization = lazyRetry(() => import('@/pages/ai/AIPickOptimization'));
const AIInventoryReplenishment = lazyRetry(() => import('@/pages/ai/AIInventoryReplenishment'));
const AISlottingAdvisor = lazyRetry(() => import('@/pages/ai/AISlottingAdvisor'));
const AIContractAnalyzer = lazyRetry(() => import('@/pages/ai/AIContractAnalyzer'));
const AIContractNegotiation = lazyRetry(() => import('@/pages/ai/AIContractNegotiation'));
const MyDay = lazyRetry(() => import('@/pages/ai/MyDay'));

// Admin (new)
const SSOManagement = lazyRetry(() => import('@/pages/admin/SSOManagement'));
const OAuthConfig = lazyRetry(() => import('@/pages/admin/OAuthConfig'));
const APIGateway = lazyRetry(() => import('@/pages/admin/APIGateway'));
const NotificationChannels = lazyRetry(() => import('@/pages/admin/NotificationChannels'));
const KeyRotation = lazyRetry(() => import('@/pages/admin/KeyRotation'));
const Archival = lazyRetry(() => import('@/pages/admin/Archival'));
const DataMigration = lazyRetry(() => import('@/pages/admin/DataMigration'));
const GPMigration = lazyRetry(() => import('@/pages/admin/GPMigration'));
const SearchConfig = lazyRetry(() => import('@/pages/admin/SearchConfig'));
const HealthChecks = lazyRetry(() => import('@/pages/admin/HealthChecks'));
const DocuSign = lazyRetry(() => import('@/pages/admin/DocuSign'));
const SlackWebhooks = lazyRetry(() => import('@/pages/admin/SlackWebhooks'));

// Reports (new)
const BIDashboard = lazyRetry(() => import('@/pages/reports/BIDashboard'));
const ReportBuilder = lazyRetry(() => import('@/pages/reports/ReportBuilder'));
const AsyncReports = lazyRetry(() => import('@/pages/reports/AsyncReports'));

// FreshByte-surpass modules
const CatchWeight = lazyRetry(() => import('@/pages/pricing/CatchWeight'));
const DSDDashboard = lazyRetry(() => import('@/pages/dsd/DSDDashboard'));
const RouteManagement = lazyRetry(() => import('@/pages/dsd/RouteManagement'));
const TruckManagement = lazyRetry(() => import('@/pages/dsd/TruckManagement'));
const AdvancedPricing = lazyRetry(() => import('@/pages/pricing/AdvancedPricing'));
const BidPricing = lazyRetry(() => import('@/pages/pricing/BidPricing'));
const CashDesk = lazyRetry(() => import('@/pages/pos/CashDesk'));
const MultiCostLayers = lazyRetry(() => import('@/pages/inventory/MultiCostLayers'));
const AccruedPayables = lazyRetry(() => import('@/pages/ap/AccruedPayables'));
const BouncedChecks = lazyRetry(() => import('@/pages/ap/BouncedChecks'));
const NewInvoice = lazyRetry(() => import('@/pages/ap/NewInvoice'));
const CheckRun = lazyRetry(() => import('@/pages/ap/CheckRun'));
const FieldVisits = lazyRetry(() => import('@/pages/sales/FieldVisits'));
const CashReceipts = lazyRetry(() => import('@/pages/sales/CashReceipts'));
const BankDeposits = lazyRetry(() => import('@/pages/accounting/BankDeposits'));
const PriceAfterSale = lazyRetry(() => import('@/pages/pricing/PriceAfterSale'));

// Time & Attendance (new)
const TATimesheets = lazyRetry(() => import('@/pages/ta/TimesheetManagement'));
const TAPTOManagement = lazyRetry(() => import('@/pages/ta/PTOManagement'));
const TADeviceManagement = lazyRetry(() => import('@/pages/ta/DeviceManagement'));
const TAAttendanceReports = lazyRetry(() => import('@/pages/ta/AttendanceReports'));
const TAScheduleCalendar = lazyRetry(() => import('@/pages/ta/ScheduleCalendar'));
const TAGeofenceManagement = lazyRetry(() => import('@/pages/ta/GeofenceManagement'));

// Manufacturing
const ManufacturingOrders = lazyRetry(() => import('@/pages/manufacturing/ManufacturingOrders'));
const BOMManagement = lazyRetry(() => import('@/pages/manufacturing/BOMManagement'));
const WorkCenters = lazyRetry(() => import('@/pages/manufacturing/WorkCenters'));
const ECOManagement = lazyRetry(() => import('@/pages/manufacturing/ECOManagement'));
const ProductionTracking = lazyRetry(() => import('@/pages/manufacturing/ProductionTracking'));

// Service Desk
const SupportTickets = lazyRetry(() => import('@/pages/service/SupportTickets'));
const AgentDesktop = lazyRetry(() => import('@/pages/service/AgentDesktop'));
const KnowledgeBase = lazyRetry(() => import('@/pages/service/KnowledgeBase'));

// Marketing
const SocialMediaConnections = lazyRetry(() => import('@/pages/marketing/SocialMediaConnections'));
const SocialMediaCallback = lazyRetry(() => import('@/pages/marketing/SocialMediaCallback'));

// Automation
const AutomationDashboard = lazyRetry(() => import('@/pages/automation/AutomationDashboard'));
const AutomationHub = lazyRetry(() => import('@/pages/automation/AutomationHub'));
const WorkflowMonitor = lazyRetry(() => import('@/pages/automation/WorkflowMonitor'));
const RecurringManagement = lazyRetry(() => import('@/pages/automation/RecurringManagement'));
const WorkflowRules = lazyRetry(() => import('@/pages/automation/WorkflowRules'));

// Compliance (missing route)
const ComplianceScheduler = lazyRetry(() => import('@/pages/compliance/ComplianceScheduler'));

// Kiosk
const KioskDevices = lazyRetry(() => import('@/pages/kiosk/KioskDevices'));

// Equipment Maintenance
const EquipmentMaintenance = lazyRetry(() => import('@/pages/maintenance/EquipmentMaintenance'));

// Time & Attendance (new pages)
const TAShiftSwaps = lazyRetry(() => import('@/pages/ta/ShiftSwapManagement'));
const TAPTOPolicies = lazyRetry(() => import('@/pages/ta/PTOPolicyAdmin'));
const TATeamCalendar = lazyRetry(() => import('@/pages/ta/TeamCalendar'));
const TAIntelligence = lazyRetry(() => import('@/pages/ta/TAIntelligence'));

function LazyPage({ children }: { children: React.ReactNode }) {
  // Clear the reload flag once a lazy chunk loads successfully
  sessionStorage.removeItem('chunk-reload');
  return (
    <Suspense fallback={<LoadingOverlay visible />}>
      {children}
    </Suspense>
  );
}

const router = createBrowserRouter([
  {
    path: '/welcome',
    element: <ComingSoon />,
  },
  {
    path: '/',
    element: <ComingSoon />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/forgot-password',
    element: <LazyPage><ForgotPassword /></LazyPage>,
  },
  {
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      { path: 'dashboard', element: <Dashboard /> },

      // Inventory
      { path: 'inventory/products', element: <LazyPage><Products /></LazyPage> },
      { path: 'inventory/stock-levels', element: <LazyPage><StockLevels /></LazyPage> },
      { path: 'inventory/receiving', element: <LazyPage><Receiving /></LazyPage> },
      { path: 'inventory/pallets', element: <LazyPage><Pallets /></LazyPage> },
      { path: 'inventory/physical-count', element: <LazyPage><PhysicalCount /></LazyPage> },
      { path: 'inventory/expiration', element: <LazyPage><Expiration /></LazyPage> },

      // Sales
      { path: 'sales/dashboard', element: <LazyPage><SalesDashboard /></LazyPage> },
      { path: 'sales/orders', element: <LazyPage><Orders /></LazyPage> },
      { path: 'sales/new-order', element: <LazyPage><NewOrder /></LazyPage> },
      { path: 'sales/customers', element: <LazyPage><Customers /></LazyPage> },
      { path: 'sales/customer-portal', element: <LazyPage><CustomerPortal /></LazyPage> },
      { path: 'sales/credit-memos', element: <LazyPage><CreditMemos /></LazyPage> },
      { path: 'sales/payments', element: <LazyPage><Payments /></LazyPage> },
      { path: 'sales/cash-receipts', element: <LazyPage><CashReceipts /></LazyPage> },
      { path: 'sales/order-intake', element: <LazyPage><PermissionGuard permission="sales.order_intake"><OrderIntakeQueue /></PermissionGuard></LazyPage> },
      { path: 'sales/ai-customer', element: <LazyPage><PermissionGuard permission="sales.claims"><AICustomerDashboard /></PermissionGuard></LazyPage> },

      // Purchasing
      { path: 'purchasing/purchase-orders', element: <LazyPage><PurchaseOrders /></LazyPage> },
      { path: 'purchasing/vendors', element: <LazyPage><Vendors /></LazyPage> },
      { path: 'purchasing/vendor-portal', element: <LazyPage><VendorPortal /></LazyPage> },
      { path: 'purchasing/landed-costs', element: <LazyPage><LandedCosts /></LazyPage> },
      { path: 'purchasing/vendor-catalog', element: <LazyPage><VendorCatalog /></LazyPage> },

      // Accounting
      { path: 'accounting/gl-entries', element: <LazyPage><GLEntries /></LazyPage> },
      { path: 'accounting/journal-entries', element: <LazyPage><JournalEntries /></LazyPage> },
      { path: 'accounting/journal-entries/new', element: <LazyPage><NewJournalEntry /></LazyPage> },
      { path: 'accounting/trial-balance', element: <LazyPage><TrialBalance /></LazyPage> },
      { path: 'accounting/bank-reconciliation', element: <LazyPage><BankReconciliation /></LazyPage> },
      { path: 'accounting/reports', element: <LazyPage><AccountingReports /></LazyPage> },
      { path: 'accounting/fixed-assets', element: <LazyPage><FixedAssets /></LazyPage> },
      { path: 'accounting/budgets', element: <LazyPage><Budgets /></LazyPage> },
      { path: 'accounting/check-setup', element: <LazyPage><CheckSetup /></LazyPage> },
      { path: 'accounting/cpa-audit-package', element: <LazyPage><CPAAuditPackage /></LazyPage> },
      { path: 'accounting/audit-readiness', element: <LazyPage><AuditReadiness /></LazyPage> },
      { path: 'accounting/bank-import', element: <LazyPage><BankStatementImport /></LazyPage> },
      { path: 'accounting/investments', element: <LazyPage><InvestmentPortfolio /></LazyPage> },
      { path: 'accounting/credit-management', element: <LazyPage><CreditManagement /></LazyPage> },
      { path: 'accounting/recurring-entries', element: <LazyPage><RecurringEntries /></LazyPage> },
      { path: 'accounting/banking', element: <LazyPage><Banking /></LazyPage> },
      { path: 'accounting/bank-deposits', element: <LazyPage><BankDeposits /></LazyPage> },
      { path: 'accounting/currency', element: <LazyPage><CurrencyRates /></LazyPage> },
      { path: 'accounting/fiscal-periods', element: <LazyPage><FiscalPeriods /></LazyPage> },
      { path: 'accounting/tax-compliance', element: <LazyPage><TaxCompliance /></LazyPage> },

      // CRM
      { path: 'crm/pipeline', element: <LazyPage><CRMPipeline /></LazyPage> },
      { path: 'crm/contacts', element: <LazyPage><Contacts /></LazyPage> },
      { path: 'crm/activities', element: <LazyPage><Activities /></LazyPage> },

      // Operations
      { path: 'operations/employees', element: <LazyPage><Employees /></LazyPage> },
      { path: 'operations/pick-lists', element: <LazyPage><PickLists /></LazyPage> },
      { path: 'operations/cycle-counts', element: <LazyPage><CycleCounts /></LazyPage> },
      { path: 'operations/shipping', element: <LazyPage><Shipping /></LazyPage> },
      { path: 'operations/receiving', element: <LazyPage><ReceivingDock /></LazyPage> },
      { path: 'operations/cold-chain-monitor', element: <LazyPage><ColdChainMonitor /></LazyPage> },
      { path: 'operations/route-optimization', element: <LazyPage><RouteOptimization /></LazyPage> },
      { path: 'operations/scheduling', element: <LazyPage><Scheduling /></LazyPage> },
      { path: 'operations/alerts', element: <LazyPage><PermissionGuard permission="ops.feedback.read"><OperationalAlerts /></PermissionGuard></LazyPage> },

      // Compliance
      { path: 'compliance/food-safety', element: <LazyPage><FoodSafety /></LazyPage> },
      { path: 'compliance/paca', element: <LazyPage><PACA /></LazyPage> },
      { path: 'compliance/cold-chain', element: <LazyPage><ColdChain /></LazyPage> },
      { path: 'compliance/haccp', element: <LazyPage><HACCPPlans /></LazyPage> },
      { path: 'compliance/gap-gmp', element: <LazyPage><GAPInspections /></LazyPage> },
      { path: 'compliance/gfsi', element: <LazyPage><GFSICertification /></LazyPage> },
      { path: 'compliance/allergen', element: <LazyPage><AllergenControl /></LazyPage> },
      { path: 'compliance/env-monitoring', element: <LazyPage><EnvironmentalMonitoring /></LazyPage> },
      { path: 'compliance/food-defense', element: <LazyPage><FoodDefense /></LazyPage> },
      { path: 'compliance/fsma', element: <LazyPage><FSMAControls /></LazyPage> },
      { path: 'compliance/organic', element: <LazyPage><OrganicCOC /></LazyPage> },
      { path: 'compliance/pesticide-mrl', element: <LazyPage><PesticideMRL /></LazyPage> },
      { path: 'compliance/lgma', element: <LazyPage><LGMACompliance /></LazyPage> },
      { path: 'compliance/audit-connectors', element: <LazyPage><AuditConnectors /></LazyPage> },
      { path: 'compliance/internal-audit', element: <LazyPage><InternalAudit /></LazyPage> },
      { path: 'compliance/calibration', element: <LazyPage><Calibration /></LazyPage> },
      { path: 'compliance/change-control', element: <LazyPage><ChangeControl /></LazyPage> },
      { path: 'compliance/customer-compliance', element: <LazyPage><CustomerCompliance /></LazyPage> },

      // Pricing
      { path: 'pricing', element: <LazyPage><PriceManagement /></LazyPage> },

      // Forecasting
      { path: 'forecasting', element: <LazyPage><Forecasting /></LazyPage> },

      // Reports
      { path: 'reports/dashboards', element: <LazyPage><Dashboards /></LazyPage> },
      { path: 'reports/financial-dashboard', element: <LazyPage><FinancialDashboard /></LazyPage> },
      { path: 'operations/daily-summary', element: <LazyPage><DailySummary /></LazyPage> },

      // Admin
      { path: 'admin/hub', element: <LazyPage><AdminHub /></LazyPage> },
      { path: 'admin/users', element: <LazyPage><Users /></LazyPage> },
      { path: 'admin/roles', element: <LazyPage><Roles /></LazyPage> },
      { path: 'admin/audit-log', element: <LazyPage><AuditLog /></LazyPage> },
      { path: 'admin/settings', element: <LazyPage><AdminSettings /></LazyPage> },
      { path: 'admin/api-tokens', element: <LazyPage><ApiTokens /></LazyPage> },
      { path: 'admin/documents', element: <LazyPage><Documents /></LazyPage> },
      { path: 'admin/system-health', element: <LazyPage><SystemHealth /></LazyPage> },
      { path: 'admin/webhooks', element: <LazyPage><AdminWebhooks /></LazyPage> },
      { path: 'admin/tenants', element: <LazyPage><Tenants /></LazyPage> },

      // Payroll & HR
      { path: 'payroll', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollDashboard /></PermissionGuard></LazyPage> },
      { path: 'payroll/runs', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollRuns /></PermissionGuard></LazyPage> },
      { path: 'payroll/runs/:runId/employees', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollRunEmployees /></PermissionGuard></LazyPage> },
      { path: 'payroll/setup', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollSetup /></PermissionGuard></LazyPage> },
      { path: 'payroll/pay-stubs', element: <LazyPage><PermissionGuard permission="payroll.view"><PayStubs /></PermissionGuard></LazyPage> },
      { path: 'payroll/time-attendance', element: <LazyPage><PermissionGuard permission="payroll.view"><TimeAttendance /></PermissionGuard></LazyPage> },
      { path: 'payroll/advanced', element: <LazyPage><PermissionGuard permission="payroll.view"><AdvancedPayroll /></PermissionGuard></LazyPage> },
      { path: 'hr/recruiting', element: <LazyPage><Recruiting /></LazyPage> },

      // AP Automation
      { path: 'ap', element: <LazyPage><APDashboard /></LazyPage> },
      { path: 'ap/invoice-capture', element: <LazyPage><InvoiceCapture /></LazyPage> },
      { path: 'ap/corporate-cards', element: <LazyPage><CorporateCards /></LazyPage> },
      { path: 'ap/cash-forecast', element: <LazyPage><CashForecast /></LazyPage> },
      { path: 'ap/exception-queue', element: <LazyPage><ExceptionQueue /></LazyPage> },

      // Fleet & DOT
      { path: 'fleet', element: <LazyPage><FleetDashboard /></LazyPage> },
      { path: 'fleet/maintenance', element: <LazyPage><FleetMaintenance /></LazyPage> },
      { path: 'fleet/dot-compliance', element: <LazyPage><DOTCompliance /></LazyPage> },
      { path: 'fleet/asset-tracking', element: <LazyPage><AssetTracking /></LazyPage> },
      { path: 'fleet/driver-safety', element: <LazyPage><DriverSafety /></LazyPage> },

      // Workflow & Integrations
      { path: 'workflow/templates', element: <LazyPage><WorkflowTemplates /></LazyPage> },
      { path: 'workflow/instances', element: <LazyPage><WorkflowInstances /></LazyPage> },
      { path: 'integrations/edi', element: <LazyPage><EDIManagement /></LazyPage> },

      // AI
      { path: 'ai/documents', element: <LazyPage><DocumentIntelligence /></LazyPage> },

      // Pricing (extended)
      { path: 'pricing/price-sheets', element: <LazyPage><PriceSheets /></LazyPage> },
      { path: 'pricing/dynamic-discounts', element: <LazyPage><DynamicDiscounts /></LazyPage> },
      { path: 'pricing/rebates', element: <LazyPage><Rebates /></LazyPage> },

      // IoT
      { path: 'iot/sensors', element: <LazyPage><IoTSensors /></LazyPage> },

      // Inventory Intelligence
      { path: 'inventory/spoilage-risk', element: <LazyPage><SpoilageRisk /></LazyPage> },
      { path: 'inventory/shrink-analytics', element: <LazyPage><ShrinkAnalytics /></LazyPage> },

      // Compliance (extended)
      { path: 'compliance/recalls', element: <LazyPage><RecallManagement /></LazyPage> },

      // Logistics
      { path: 'logistics/3pl', element: <LazyPage><ThreePLManagement /></LazyPage> },
      { path: 'logistics/ml', element: <LazyPage><LogisticsML /></LazyPage> },

      // Audit & Security
      { path: 'audit/auditor-portal', element: <LazyPage><AuditorPortal /></LazyPage> },
      { path: 'audit/integrity', element: <LazyPage><AuditIntegrityDashboard /></LazyPage> },
      { path: 'admin/sod', element: <LazyPage><SODAdministration /></LazyPage> },
      { path: 'admin/access-review', element: <LazyPage><AccessReview /></LazyPage> },
      { path: 'admin/encryption-keys', element: <LazyPage><EncryptionKeyManagement /></LazyPage> },
      { path: 'admin/data-retention', element: <LazyPage><DataRetention /></LazyPage> },
      { path: 'payroll/tax-reconciliation', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollTaxReconciliation /></PermissionGuard></LazyPage> },
      { path: 'payroll/benefits-reconciliation', element: <LazyPage><PermissionGuard permission="payroll.view"><BenefitsReconciliation /></PermissionGuard></LazyPage> },
      { path: 'admin/session-security', element: <LazyPage><SessionSecurity /></LazyPage> },
      { path: 'admin/break-glass', element: <LazyPage><BreakGlassAccess /></LazyPage> },
      { path: 'admin/dual-approval', element: <LazyPage><DualApproval /></LazyPage> },
      { path: 'audit/compliance-evidence', element: <LazyPage><ComplianceEvidence /></LazyPage> },
      { path: 'admin/pentest-evidence', element: <LazyPage><PentestEvidence /></LazyPage> },
      { path: 'admin/siem', element: <LazyPage><SIEMIntegration /></LazyPage> },
      { path: 'admin/data-classification', element: <LazyPage><DataClassification /></LazyPage> },
      { path: 'admin/control-testing', element: <LazyPage><ControlTesting /></LazyPage> },
      { path: 'admin/period-close', element: <LazyPage><PeriodCloseHub /></LazyPage> },

      // Finance
      { path: 'finance/petty-cash', element: <LazyPage><PettyCash /></LazyPage> },
      { path: 'finance/intercompany', element: <LazyPage><Intercompany /></LazyPage> },
      { path: 'finance/financial-close', element: <LazyPage><FinancialClose /></LazyPage> },
      { path: 'finance/lease-accounting', element: <LazyPage><LeaseAccounting /></LazyPage> },
      { path: 'finance/cash-application', element: <LazyPage><CashApplication /></LazyPage> },
      { path: 'finance/consolidated-tax', element: <LazyPage><ConsolidatedTax /></LazyPage> },
      { path: 'finance/deferred-tax', element: <LazyPage><DeferredTax /></LazyPage> },
      { path: 'finance/cost-centers', element: <LazyPage><CostCenters /></LazyPage> },
      { path: 'finance/revenue', element: <LazyPage><RevenueRecognition /></LazyPage> },
      { path: 'finance/bad-debt', element: <LazyPage><BadDebt /></LazyPage> },
      { path: 'finance/ap-aging', element: <LazyPage><APAging /></LazyPage> },
      { path: 'finance/ap-duplicates', element: <LazyPage><APDuplicates /></LazyPage> },
      { path: 'finance/bank-import', element: <LazyPage><BankImport /></LazyPage> },
      { path: 'finance/plaid', element: <LazyPage><PlaidFeeds /></LazyPage> },
      { path: 'finance/check-wire', element: <LazyPage><CheckWire /></LazyPage> },
      { path: 'finance/payment-links', element: <LazyPage><PaymentLinksPage /></LazyPage> },
      { path: 'finance/payment-orchestrator', element: <LazyPage><PaymentOrchestrator /></LazyPage> },
      { path: 'finance/payment-terms', element: <LazyPage><PaymentTerms /></LazyPage> },
      { path: 'finance/chargebacks', element: <LazyPage><ChargebacksPage /></LazyPage> },
      { path: 'finance/credit-scoring', element: <LazyPage><CreditScoring /></LazyPage> },
      { path: 'finance/expenses', element: <LazyPage><ExpenseReimbursement /></LazyPage> },
      { path: 'finance/form-8300', element: <LazyPage><Form8300 /></LazyPage> },
      { path: 'finance/form-4562', element: <LazyPage><Form4562 /></LazyPage> },
      { path: 'finance/schedule-m1', element: <LazyPage><ScheduleM1 /></LazyPage> },

      // Reconciliation modules
      { path: 'finance/credit-card-recon', element: <LazyPage><CreditCardReconciliation /></LazyPage> },
      { path: 'finance/sales-tax-recon', element: <LazyPage><SalesTaxReconciliation /></LazyPage> },
      { path: 'finance/vendor-statement-recon', element: <LazyPage><VendorStatementReconciliation /></LazyPage> },
      { path: 'accounting/sub-ledger-dashboard', element: <LazyPage><SubLedgerDashboard /></LazyPage> },
      { path: 'accounting/fixed-asset-recon', element: <LazyPage><FixedAssetReconciliation /></LazyPage> },

      // ERP expansion modules
      { path: 'finance/project-accounting', element: <LazyPage><ProjectAccounting /></LazyPage> },
      { path: 'hr/employee-self-service', element: <LazyPage><EmployeeSelfService /></LazyPage> },
      { path: 'hr/ess-admin', element: <LazyPage><ESSAdmin /></LazyPage> },
      { path: 'purchasing/vendor-portal', element: <LazyPage><VendorPortalManagement /></LazyPage> },
      { path: 'purchasing/requisitions', element: <LazyPage><PurchaseRequisitions /></LazyPage> },
      { path: 'sales/returns', element: <LazyPage><ReturnsManagement /></LazyPage> },
      { path: 'sales/warranty', element: <LazyPage><WarrantyTracking /></LazyPage> },
      { path: 'accounting/multi-book', element: <LazyPage><MultiBookAccounting /></LazyPage> },
      { path: 'operations/sla-tracking', element: <LazyPage><SLATracking /></LazyPage> },
      { path: 'operations/change-management', element: <LazyPage><ChangeManagement /></LazyPage> },

      // Payroll (extended)
      { path: 'payroll/ach', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollACH /></PermissionGuard></LazyPage> },
      { path: 'payroll/register', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollRegister /></PermissionGuard></LazyPage> },
      { path: 'payroll/garnishments', element: <LazyPage><PermissionGuard permission="payroll.view"><Garnishments /></PermissionGuard></LazyPage> },
      { path: 'payroll/retro-pay', element: <LazyPage><PermissionGuard permission="payroll.view"><RetroPay /></PermissionGuard></LazyPage> },
      { path: 'payroll/tips', element: <LazyPage><PermissionGuard permission="payroll.view"><TipManagement /></PermissionGuard></LazyPage> },
      { path: 'payroll/international', element: <LazyPage><PermissionGuard permission="payroll.view"><InternationalPayroll /></PermissionGuard></LazyPage> },
      { path: 'payroll/state-wages', element: <LazyPage><PermissionGuard permission="payroll.view"><StateWages /></PermissionGuard></LazyPage> },
      { path: 'payroll/suta', element: <LazyPage><PermissionGuard permission="payroll.view"><SUTAFiling /></PermissionGuard></LazyPage> },
      { path: 'payroll/reciprocity', element: <LazyPage><PermissionGuard permission="payroll.view"><StateReciprocity /></PermissionGuard></LazyPage> },
      { path: 'payroll/certified', element: <LazyPage><PermissionGuard permission="payroll.view"><CertifiedPayroll /></PermissionGuard></LazyPage> },
      { path: 'payroll/cse', element: <LazyPage><PermissionGuard permission="payroll.view"><CSERemittance /></PermissionGuard></LazyPage> },
      { path: 'payroll/new-hire', element: <LazyPage><PermissionGuard permission="payroll.view"><NewHireReporting /></PermissionGuard></LazyPage> },
      { path: 'payroll/ewa', element: <LazyPage><PermissionGuard permission="payroll.view"><EarnedWageAccess /></PermissionGuard></LazyPage> },
      { path: 'payroll/form-940', element: <LazyPage><PermissionGuard permission="payroll.view"><Form940 /></PermissionGuard></LazyPage> },
      { path: 'payroll/form-941', element: <LazyPage><PermissionGuard permission="payroll.view"><Form941 /></PermissionGuard></LazyPage> },
      { path: 'payroll/form-943', element: <LazyPage><PermissionGuard permission="payroll.view"><Form943 /></PermissionGuard></LazyPage> },
      { path: 'payroll/w2', element: <LazyPage><PermissionGuard permission="payroll.view"><W2Management /></PermissionGuard></LazyPage> },
      { path: 'payroll/w4', element: <LazyPage><PermissionGuard permission="payroll.view"><W4Tracking /></PermissionGuard></LazyPage> },
      { path: 'payroll/1099', element: <LazyPage><PermissionGuard permission="payroll.view"><Form1099 /></PermissionGuard></LazyPage> },
      { path: 'payroll/import', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollImport /></PermissionGuard></LazyPage> },
      { path: 'payroll/wizard', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollRunWizard /></PermissionGuard></LazyPage> },
      { path: 'payroll/new-run', element: <LazyPage><PermissionGuard permission="payroll.view"><NewPayrollRun /></PermissionGuard></LazyPage> },
      { path: 'payroll/reports', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollReports /></PermissionGuard></LazyPage> },
      { path: 'payroll/direct-deposit', element: <LazyPage><PermissionGuard permission="payroll.view"><DirectDeposit /></PermissionGuard></LazyPage> },
      { path: 'payroll/audit-log', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollAuditLog /></PermissionGuard></LazyPage> },
      { path: 'payroll/alerts', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollAlerts /></PermissionGuard></LazyPage> },
      { path: 'payroll/ai', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollAIAssistant /></PermissionGuard></LazyPage> },
      { path: 'payroll/ai/anomalies', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollAnomalyDetection /></PermissionGuard></LazyPage> },
      { path: 'payroll/ai/corrections', element: <LazyPage><PermissionGuard permission="payroll.view"><PayrollBatchCorrections /></PermissionGuard></LazyPage> },
      { path: 'payroll/automation', element: <LazyPage><PermissionGuard permission="payroll.admin"><PayrollAutomation /></PermissionGuard></LazyPage> },
      { path: 'payroll/ai-approval', element: <LazyPage><PermissionGuard permission="payroll.admin"><PayrollAIApproval /></PermissionGuard></LazyPage> },
      { path: 'payroll/gl-reconciliation', element: <LazyPage><PermissionGuard permission="payroll.admin"><PayrollGLReconciliation /></PermissionGuard></LazyPage> },
      { path: 'payroll/benefits-vendor-recon', element: <LazyPage><PermissionGuard permission="payroll.admin"><BenefitsVendorRecon /></PermissionGuard></LazyPage> },
      { path: 'payroll/bank-recon', element: <LazyPage><PermissionGuard permission="payroll.admin"><PayrollBankRecon /></PermissionGuard></LazyPage> },

      // HR
      { path: 'hr/aca', element: <LazyPage><ACACompliance /></LazyPage> },
      { path: 'hr/401k', element: <LazyPage><Retirement401k /></LazyPage> },
      { path: 'hr/erisa', element: <LazyPage><ERISACompliance /></LazyPage> },
      { path: 'hr/leave-benefits', element: <LazyPage><LeaveBenefits /></LazyPage> },
      { path: 'hr/onboarding', element: <LazyPage><Onboarding /></LazyPage> },
      { path: 'hr/lms', element: <LazyPage><LMS /></LazyPage> },

      // TMS
      { path: 'tms/advanced', element: <LazyPage><TMSAdvanced /></LazyPage> },
      { path: 'tms/intelligence', element: <LazyPage><TMSIntelligence /></LazyPage> },
      { path: 'tms/rate-shopping', element: <LazyPage><RateShopping /></LazyPage> },
      { path: 'tms/freight-audit', element: <LazyPage><FreightAudit /></LazyPage> },
      { path: 'tms/load-planning', element: <LazyPage><LoadPlanning /></LazyPage> },
      { path: 'tms/route-accounting', element: <LazyPage><RouteAccounting /></LazyPage> },

      // Fleet
      { path: 'fleet/tms', element: <LazyPage><FleetTMS /></LazyPage> },
      { path: 'fleet/driver-performance', element: <LazyPage><DriverPerformance /></LazyPage> },
      { path: 'fleet/dot-testing', element: <LazyPage><DrugAlcoholTesting /></LazyPage> },
      { path: 'fleet/dashcam', element: <LazyPage><DashcamIntegration /></LazyPage> },

      // Warehouse
      { path: 'warehouse/wms', element: <LazyPage><WMS /></LazyPage> },
      { path: 'warehouse/advanced', element: <LazyPage><WarehouseAdvanced /></LazyPage> },
      { path: 'warehouse/dock-scheduling', element: <LazyPage><DockScheduling /></LazyPage> },
      { path: 'warehouse/yard', element: <LazyPage><YardManagement /></LazyPage> },
      { path: 'warehouse/slotting', element: <LazyPage><Slotting /></LazyPage> },
      { path: 'warehouse/allocation', element: <LazyPage><Allocation /></LazyPage> },
      { path: 'warehouse/mrp', element: <LazyPage><MRP /></LazyPage> },
      { path: 'warehouse/pack-house', element: <LazyPage><PackHouse /></LazyPage> },
      { path: 'warehouse/pallet-build', element: <LazyPage><PalletBuild /></LazyPage> },
      { path: 'warehouse/shelf-life', element: <LazyPage><ShelfLife /></LazyPage> },
      { path: 'warehouse/robotics', element: <LazyPage><Robotics /></LazyPage> },

      // Supply Chain
      { path: 'supply-chain/grading', element: <LazyPage><Grading /></LazyPage> },
      { path: 'supply-chain/cool', element: <LazyPage><CoolCompliance /></LazyPage> },
      { path: 'supply-chain/ethylene-ca', element: <LazyPage><EthyleneCA /></LazyPage> },
      { path: 'supply-chain/freshness', element: <LazyPage><Freshness /></LazyPage> },
      { path: 'supply-chain/trace', element: <LazyPage><Trace /></LazyPage> },
      { path: 'supply-chain/mock-recall', element: <LazyPage><MockRecall /></LazyPage> },

      // Commerce
      { path: 'commerce/ecommerce', element: <LazyPage><ECommerce /></LazyPage> },
      { path: 'commerce/broker-commission', element: <LazyPage><BrokerCommission /></LazyPage> },
      { path: 'commerce/loyalty', element: <LazyPage><LoyaltyRebates /></LazyPage> },
      { path: 'commerce/rfq', element: <LazyPage><RFQ /></LazyPage> },
      { path: 'commerce/recurring', element: <LazyPage><RecurringOrders /></LazyPage> },
      { path: 'commerce/pos', element: <LazyPage><POS /></LazyPage> },
      { path: 'commerce/substitutions', element: <LazyPage><Substitutions /></LazyPage> },
      { path: 'commerce/price-locks', element: <LazyPage><PriceLocks /></LazyPage> },
      { path: 'commerce/predictive-pricing', element: <LazyPage><PredictivePricing /></LazyPage> },
      { path: 'commerce/menu-planning', element: <LazyPage><MenuPlanning /></LazyPage> },
      { path: 'commerce/customer-preferences', element: <LazyPage><CustomerPreferences /></LazyPage> },

      // Compliance (extended)
      { path: 'compliance/permits', element: <LazyPage><Permits /></LazyPage> },
      { path: 'compliance/paca-trust', element: <LazyPage><PACATrust /></LazyPage> },
      { path: 'compliance/bioterrorism', element: <LazyPage><Bioterrorism /></LazyPage> },
      { path: 'compliance/bcp', element: <LazyPage><BCP /></LazyPage> },
      { path: 'compliance/cfats', element: <LazyPage><CFATS /></LazyPage> },
      { path: 'compliance/carbon-esg', element: <LazyPage><CarbonESG /></LazyPage> },
      { path: 'compliance/commodity-assessments', element: <LazyPage><CommodityAssessments /></LazyPage> },
      { path: 'compliance/data-breach', element: <LazyPage><DataBreach /></LazyPage> },
      { path: 'compliance/fcra', element: <LazyPage><FCRA /></LazyPage> },
      { path: 'compliance/fda-furls', element: <LazyPage><FDAFurls /></LazyPage> },
      { path: 'compliance/fda-registration', element: <LazyPage><FDARegistration /></LazyPage> },
      { path: 'compliance/fsma-transport', element: <LazyPage><FSMATransport /></LazyPage> },
      { path: 'compliance/import-duty', element: <LazyPage><ImportDuty /></LazyPage> },
      { path: 'compliance/marketing', element: <LazyPage><MarketingCompliance /></LazyPage> },
      { path: 'compliance/ofac', element: <LazyPage><OFACSanctions /></LazyPage> },

      // Regulatory
      { path: 'regulatory/organic-enforcement', element: <LazyPage><OrganicEnforcement /></LazyPage> },
      { path: 'regulatory/pci', element: <LazyPage><PCICompliance /></LazyPage> },
      { path: 'regulatory/phytosanitary', element: <LazyPage><Phytosanitary /></LazyPage> },
      { path: 'regulatory/privacy', element: <LazyPage><Privacy /></LazyPage> },
      { path: 'regulatory/state-organic', element: <LazyPage><StateOrganic /></LazyPage> },
      { path: 'regulatory/userra', element: <LazyPage><USERRA /></LazyPage> },
      { path: 'regulatory/warn-act', element: <LazyPage><WARNAct /></LazyPage> },
      { path: 'regulatory/weights-measures', element: <LazyPage><WeightsMeasures /></LazyPage> },

      // VA Tax
      { path: 'va-tax', element: <LazyPage><VirginiaCompliance /></LazyPage> },
      { path: 'va-tax/st1', element: <LazyPage><VAST1 /></LazyPage> },
      { path: 'va-tax/va4', element: <LazyPage><VA4Certificate /></LazyPage> },
      { path: 'va-tax/va5', element: <LazyPage><VA5Filing /></LazyPage> },
      { path: 'va-tax/va15', element: <LazyPage><VA15Deposit /></LazyPage> },
      { path: 'va-tax/va16', element: <LazyPage><VA16Reconciliation /></LazyPage> },
      { path: 'va-tax/withholding', element: <LazyPage><VAWithholding /></LazyPage> },
      { path: 'va-tax/corporate', element: <LazyPage><VACorporateTax /></LazyPage> },
      { path: 'va-tax/ifta', element: <LazyPage><VAIFTA /></LazyPage> },
      { path: 'va-tax/filing-frequency', element: <LazyPage><VAFilingFrequency /></LazyPage> },
      { path: 'va-tax/penalty-interest', element: <LazyPage><VAPenaltyInterest /></LazyPage> },
      { path: 'va-tax/tangible-property', element: <LazyPage><VATangibleProperty /></LazyPage> },
      { path: 'va-tax/intelligence', element: <LazyPage><VATaxIntelligence /></LazyPage> },
      { path: 'va-tax/vdacs', element: <LazyPage><VDACS /></LazyPage> },
      { path: 'va-tax/vdh', element: <LazyPage><VDHHealth /></LazyPage> },

      // AI
      { path: 'ai/copilot', element: <LazyPage><AICopilot /></LazyPage> },
      { path: 'ai/emails', element: <LazyPage><AIEmailParsing /></LazyPage> },
      { path: 'ai/quality', element: <LazyPage><AIQuality /></LazyPage> },
      { path: 'ai/order-chat', element: <LazyPage><AIOrderChat /></LazyPage> },
      { path: 'ai/ml-forecasting', element: <LazyPage><MLForecasting /></LazyPage> },
      { path: 'ai/demand', element: <LazyPage><DemandIntelligence /></LazyPage> },
      { path: 'ai/market', element: <LazyPage><MarketIntelligence /></LazyPage> },
      { path: 'ai/voice-agent', element: <LazyPage><VoiceAgent /></LazyPage> },
      { path: 'ai/voice-agent-live', element: <LazyPage><VoiceAgentLive /></LazyPage> },
      { path: 'ai/voice-metrics', element: <LazyPage><VoiceMetrics /></LazyPage> },
      { path: 'ai/voice-prompts', element: <LazyPage><VoicePromptEditor /></LazyPage> },
      { path: 'ai/financial', element: <LazyPage><FinancialIntelligence /></LazyPage> },
      { path: 'ai/vendor-costs', element: <LazyPage><VendorCostIntelligence /></LazyPage> },
      { path: 'ai/profit', element: <LazyPage><ProfitOptimization /></LazyPage> },
      { path: 'ai/talent-matching', element: <LazyPage><AITalentMatching /></LazyPage> },
      { path: 'ai/retention-risk', element: <LazyPage><AIRetentionRisk /></LazyPage> },
      { path: 'ai/workforce-planning', element: <LazyPage><AIWorkforcePlanning /></LazyPage> },
      { path: 'ai/lead-scoring', element: <LazyPage><AILeadScoring /></LazyPage> },
      { path: 'ai/churn-prevention', element: <LazyPage><AIChurnPrevention /></LazyPage> },
      { path: 'ai/sales-forecasting', element: <LazyPage><AISalesForecasting /></LazyPage> },
      { path: 'ai/pick-optimization', element: <LazyPage><AIPickOptimization /></LazyPage> },
      { path: 'ai/inventory-replenishment', element: <LazyPage><AIInventoryReplenishment /></LazyPage> },
      { path: 'ai/slotting-advisor', element: <LazyPage><AISlottingAdvisor /></LazyPage> },
      { path: 'ai/contract-analyzer', element: <LazyPage><AIContractAnalyzer /></LazyPage> },
      { path: 'ai/contract-negotiation', element: <LazyPage><AIContractNegotiation /></LazyPage> },
      { path: 'ai/my-day', element: <LazyPage><MyDay /></LazyPage> },
      { path: 'ai/email-monitor', element: <LazyPage><EmailMonitorV2 /></LazyPage> },
      { path: 'ai/email-monitor/metrics', element: <LazyPage><EmailMonitorMetrics /></LazyPage> },
      { path: 'crm/customers/:id/timeline', element: <LazyPage><CustomerTimeline /></LazyPage> },

      // Admin (extended)
      { path: 'admin/sso', element: <LazyPage><SSOManagement /></LazyPage> },
      { path: 'admin/oauth', element: <LazyPage><OAuthConfig /></LazyPage> },
      { path: 'admin/api-gateway', element: <LazyPage><APIGateway /></LazyPage> },
      { path: 'admin/notification-channels', element: <LazyPage><NotificationChannels /></LazyPage> },
      { path: 'admin/key-rotation', element: <LazyPage><KeyRotation /></LazyPage> },
      { path: 'admin/archival', element: <LazyPage><Archival /></LazyPage> },
      { path: 'admin/data-migration', element: <LazyPage><DataMigration /></LazyPage> },
      { path: 'admin/gp-migration', element: <LazyPage><GPMigration /></LazyPage> },
      { path: 'admin/search', element: <LazyPage><SearchConfig /></LazyPage> },
      { path: 'admin/health', element: <LazyPage><HealthChecks /></LazyPage> },
      { path: 'admin/docusign', element: <LazyPage><DocuSign /></LazyPage> },
      { path: 'admin/slack', element: <LazyPage><SlackWebhooks /></LazyPage> },

      // Reports
      { path: 'reports/bi', element: <LazyPage><BIDashboard /></LazyPage> },
      { path: 'reports/builder', element: <LazyPage><ReportBuilder /></LazyPage> },
      { path: 'reports/async', element: <LazyPage><AsyncReports /></LazyPage> },

      // FreshByte-surpass modules
      { path: 'pricing/catch-weight', element: <LazyPage><CatchWeight /></LazyPage> },
      { path: 'pricing/advanced', element: <LazyPage><AdvancedPricing /></LazyPage> },
      { path: 'pricing/bids', element: <LazyPage><BidPricing /></LazyPage> },
      { path: 'pricing/price-after-sale', element: <LazyPage><PriceAfterSale /></LazyPage> },
      { path: 'dsd', element: <LazyPage><DSDDashboard /></LazyPage> },
      { path: 'dsd/routes', element: <LazyPage><RouteManagement /></LazyPage> },
      { path: 'dsd/trucks', element: <LazyPage><TruckManagement /></LazyPage> },
      { path: 'pos/cash-desk', element: <LazyPage><CashDesk /></LazyPage> },
      { path: 'inventory/multi-cost', element: <LazyPage><MultiCostLayers /></LazyPage> },
      { path: 'ap/accrued-payables', element: <LazyPage><AccruedPayables /></LazyPage> },
      { path: 'ap/bounced-checks', element: <LazyPage><BouncedChecks /></LazyPage> },
      { path: 'ap/new-invoice', element: <LazyPage><NewInvoice /></LazyPage> },
      { path: 'ap/check-run', element: <LazyPage><CheckRun /></LazyPage> },
      { path: 'sales/field-visits', element: <LazyPage><FieldVisits /></LazyPage> },

      // Time & Attendance
      { path: 'ta/timesheets', element: <LazyPage><PermissionGuard permission="hr.view"><TATimesheets /></PermissionGuard></LazyPage> },
      { path: 'ta/pto', element: <LazyPage><PermissionGuard permission="hr.view"><TAPTOManagement /></PermissionGuard></LazyPage> },
      { path: 'ta/devices', element: <LazyPage><PermissionGuard permission="hr.view"><TADeviceManagement /></PermissionGuard></LazyPage> },
      { path: 'ta/reports', element: <LazyPage><PermissionGuard permission="hr.view"><TAAttendanceReports /></PermissionGuard></LazyPage> },
      { path: 'ta/schedule', element: <LazyPage><PermissionGuard permission="hr.view"><TAScheduleCalendar /></PermissionGuard></LazyPage> },
      { path: 'ta/geofence', element: <LazyPage><PermissionGuard permission="hr.view"><TAGeofenceManagement /></PermissionGuard></LazyPage> },
      { path: 'ta/shift-swaps', element: <LazyPage><PermissionGuard permission="hr.view"><TAShiftSwaps /></PermissionGuard></LazyPage> },
      { path: 'ta/pto-policies', element: <LazyPage><PermissionGuard permission="hr.view"><TAPTOPolicies /></PermissionGuard></LazyPage> },
      { path: 'ta/team-calendar', element: <LazyPage><PermissionGuard permission="hr.view"><TATeamCalendar /></PermissionGuard></LazyPage> },
      { path: 'ta/intelligence', element: <LazyPage><PermissionGuard permission="hr.view"><TAIntelligence /></PermissionGuard></LazyPage> },

      // Manufacturing
      { path: 'manufacturing/orders', element: <LazyPage><PermissionGuard permission="manufacturing.read"><ManufacturingOrders /></PermissionGuard></LazyPage> },
      { path: 'manufacturing/boms', element: <LazyPage><PermissionGuard permission="manufacturing.read"><BOMManagement /></PermissionGuard></LazyPage> },
      { path: 'manufacturing/work-centers', element: <LazyPage><PermissionGuard permission="manufacturing.read"><WorkCenters /></PermissionGuard></LazyPage> },
      { path: 'manufacturing/ecos', element: <LazyPage><PermissionGuard permission="manufacturing.read"><ECOManagement /></PermissionGuard></LazyPage> },
      { path: 'manufacturing/tracking', element: <LazyPage><PermissionGuard permission="manufacturing.read"><ProductionTracking /></PermissionGuard></LazyPage> },

      // Service Desk
      { path: 'service/tickets', element: <LazyPage><SupportTickets /></LazyPage> },
      { path: 'service/agent-desktop', element: <LazyPage><AgentDesktop /></LazyPage> },
      { path: 'service/knowledge-base', element: <LazyPage><KnowledgeBase /></LazyPage> },

      // Marketing
      { path: 'marketing/social-media', element: <LazyPage><SocialMediaConnections /></LazyPage> },
      { path: 'marketing/social-media/callback', element: <LazyPage><SocialMediaCallback /></LazyPage> },

      // Automation
      { path: 'automation/dashboard', element: <LazyPage><PermissionGuard permission="automation.admin"><AutomationDashboard /></PermissionGuard></LazyPage> },
      { path: 'automation/hub', element: <LazyPage><AutomationHub /></LazyPage> },
      { path: 'automation/monitor', element: <LazyPage><WorkflowMonitor /></LazyPage> },
      { path: 'automation/recurring', element: <LazyPage><RecurringManagement /></LazyPage> },
      { path: 'automation/workflow-rules', element: <LazyPage><WorkflowRules /></LazyPage> },

      // Compliance (scheduler)
      { path: 'compliance/scheduler', element: <LazyPage><ComplianceScheduler /></LazyPage> },

      // Kiosk
      { path: 'kiosk/devices', element: <LazyPage><PermissionGuard permission="hr.view"><KioskDevices /></PermissionGuard></LazyPage> },

      // Equipment Maintenance
      { path: 'maintenance/equipment', element: <LazyPage><PermissionGuard permission="operations.view"><EquipmentMaintenance /></PermissionGuard></LazyPage> },

      // Catch-all
      { path: '*', element: <ComingSoon /> },
    ],
  },
]);

export default router;
